import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer } from '@angular/platform-browser';
import { ApiService } from '@quorum/api';
import { AuthenticationStateService } from '@quorum/authentication/services';
import { environment } from '@quorum/environments';
import {
  AppointmentsAssociatesAddressesQueryParameters,
  VehiclesAssociatesAddressesQueryParameters,
} from '@quorum/models/xs-query';
import {
  AppointmentsAssociatesAddress,
  Associate,
  Employee,
  Vehicle,
  VehiclesAssociatesAddress,
} from '@quorum/models/xs-resource';

import { Router } from '@angular/router';
import { BrowserControlCommunicationService } from '@applications/sha-services';
import { ServiceLaneFacade } from '@quorum/service-lane-root/facade';
import { SystemControlStateService } from '@quorum/xsr-system-control/services';
import { AuthenticatedUser } from 'libs/authentication/src/lib/+state/authentication.interfaces';
import * as moment from 'moment';
import { forkJoin, Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { ScannerDialogComponent } from '../scanner-dialog/scanner-dialog.component';
import { MotorTaxonomyService } from '../motor-mapping/motor-taxonomy.service';

@Component({
  selector: 'svc-shell',
  templateUrl: './shell.component.html',
  styleUrls: ['./shell.component.scss'],
})
export class ShellComponent implements OnInit, OnDestroy {
  vehicleSearchForm: any = this.fb.group({
    search: [],
  });
  custId = 'ZZZJIM';
  apiKey = 'U2FsdGVkX185pZvYm8IEq4ogdOhqWLfOYr4zw10oMzBfeWI9L21jp2LuJyNcfDMP';
  googleCloud: string = environment.googleCloud;
  vehicleSearchResults: Vehicle[];
  appointmentSearchResults: AppointmentsAssociatesAddress[];
  associateSearchResults: Associate[];
  loadingSearchResults = false;
  authUser$: Observable<AuthenticatedUser>;
  authEmployee$: Observable<Employee>;
  vehicleSearchFormSearchSub: Subscription;
  destroy$ = new Subject();
  showSettings = false;

  constructor(
    private fb: FormBuilder,
    private apiService: ApiService,
    private authenticationStateService: AuthenticationStateService,
    private router: Router,
    private serviceLaneFacade: ServiceLaneFacade,
    private dialog: MatDialog,
    private matIconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer,
    private snackBar: MatSnackBar,
    @Inject(DOCUMENT) private document: Document,
    private renderer: Renderer2,
    private systemControlStateService: SystemControlStateService,
    public browserControlService: BrowserControlCommunicationService,
    private motorTaxonomyService: MotorTaxonomyService
  ) {}

  ngOnInit() {
    if (!this.browserControlService.embeddedInXsellerator) {
      this.motorTaxonomyService.preload();
    }

    this.serviceLaneFacade
      .listenForToasts()
      .pipe(takeUntil(this.destroy$))
      .subscribe((toastOptions) => {
        this.snackBar.open(toastOptions.message, toastOptions.actionText, toastOptions.options);
      });

    this.authUser$ = this.authenticationStateService.selectAuthenticatedUser();
    this.authEmployee$ = this.authenticationStateService
      .selectAuthenticatedEmployee()
      .pipe(filter((employee) => employee != null));

    this.matIconRegistry.addSvgIcon(
      `barcode`,
      this.domSanitizer.bypassSecurityTrustResourceUrl('assets/barcode-scan.svg')
    );
    this.matIconRegistry.addSvgIcon(
      `no_results`,
      this.domSanitizer.bypassSecurityTrustResourceUrl('assets/no-results.svg')
    );

    this.registerCalendarIcons();

    this.authenticationStateService
      .selectAuthenticatedEmployee()
      .pipe(
        filter((emp) => emp != null),
        switchMap((employee) => {
          return this.systemControlStateService.getSystemControlValues(
            ['CL_PRINT_DECLINED_MPVI_CODES_ON_WO_INVOICE', 'CL_DEALER_COUNTRY_ID'],
            employee.rooftopId
          );
        }),
        take(1)
      )
      .subscribe((scts) => {
        this.vehicleSearchFormSearchSub = this.vehicleSearchForm
          .get('search')
          .valueChanges.pipe(
            debounceTime(300),
            tap(() => {
              this.vehicleSearchResults = [];
              this.appointmentSearchResults = [];
              this.loadingSearchResults = false;
            }),
            filter((value: string) => value && value != null && value.trim() !== ''),
            switchMap((value: string) => {
              this.loadingSearchResults = true;
              const vehicleQueryParams = new VehiclesAssociatesAddressesQueryParameters({
                statusCode: '2,3,4,I',
                search: value,
              });
              const appointmentQueryParams = new AppointmentsAssociatesAddressesQueryParameters({
                search: value,
                appointmentDateFrom: moment().subtract(6, 'months').format(),
                statusId: '1,2',
                department: 'M',
              });
              return forkJoin([
                this.apiService.get(`/v/1/reporting/vehicles/vehicles-associates-addresses`, {
                  params: vehicleQueryParams,
                }),
                this.apiService.get(`/v/1/reporting/fixed-operations/appointments-associates-addresses`, {
                  params: appointmentQueryParams,
                }),
              ]).pipe(
                map(([vehicles, appointments]) => {
                  this.vehicleSearchResults = vehicles;
                  this.appointmentSearchResults = appointments;
                  this.loadingSearchResults = false;
                })
              );
            }),
            takeUntil(this.destroy$)
          )
          .subscribe();
      });

    if (!window.google) {
      this.loadGoogleMapScript();
    }
  }

  ngOnDestroy() {
    this.vehicleSearchFormSearchSub.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadGoogleMapScript() {
    const url = `https://maps.googleapis.com/maps/api/js?key=${this.googleCloud}&libraries=places`;

    const script = this.renderer.createElement('script');
    script.type = 'text/javascript';
    script.src = url;
    script.async = true;
    script.defer = true;

    this.renderer.appendChild(this.document.body, script);
    return script;
  }

  openDialog() {
    const dialogRef = this.dialog.open(ScannerDialogComponent, {
      width: '600px',
      data: {},
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        console.log(res);
      });
  }

  //Do not believe this is used
  displaySelectedVehicle(v: Vehicle) {
    if (v) {
      return `${v.make} ${v.model}`;
    } else {
      return '';
    }
  }

  resetForm() {
    this.vehicleSearchForm.get('search').setValue(null);
  }

  logout() {
    this.authenticationStateService.logout();
  }

  selectVehicle(result: VehiclesAssociatesAddress) {
    console.log(result);
    this.router.navigate(['vehicle', result ? (result.vehicleId ? result.vehicleId : result.id) : 'new']);
  }

  selectAppointment(appointment: AppointmentsAssociatesAddress) {
    console.log(appointment);
    if (appointment.vehicleId) {
      this.router.navigate([
        `vehicle/${appointment.vehicleId}/appointments/${
          appointment.appointmentId ? appointment.appointmentId : appointment.id
        }`,
      ]);
    } else {
      this.router.navigate([`freeform/appointments/${appointment.appointmentId}`]);
    }
  }

  registerCalendarIcons() {
    for (let i = 1; i <= 31; i++) {
      this.matIconRegistry.addSvgIcon(
        `calendar_day_${i}`,
        this.domSanitizer.bypassSecurityTrustResourceUrl(`assets/calendar-icons/icons8-calendar-${i}-50.svg`)
      );
    }

    this.matIconRegistry.addSvgIcon(`tech_1`, this.domSanitizer.bypassSecurityTrustResourceUrl(`assets/tech.svg`));
    this.matIconRegistry.addSvgIcon(`tech_2`, this.domSanitizer.bypassSecurityTrustResourceUrl(`assets/tech2.svg`));
    this.matIconRegistry.addSvgIcon(`tech_3`, this.domSanitizer.bypassSecurityTrustResourceUrl(`assets/tech3.svg`));

    this.matIconRegistry.addSvgIcon(
      `calendar_empty`,
      this.domSanitizer.bypassSecurityTrustResourceUrl(`assets/calendar-icons/icons8-calendar-50.svg`)
    );
  }
}
